import { Injectable, inject } from '@angular/core';
import { AppConstants } from '@core/constants';
import { TestDto } from '@core/services/test/test-dto';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { FeatureFlagEnum } from '@shared/enums/feature-flag.enum';
import { CurveMetric } from '@shared/interfaces/curve-metric.interface';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import { FeatureFlagValue } from '@shared/services/feature-flags/interfaces/feature-flags.interface';
import { WaffleFeatureFlagsService } from '@shared/services/feature-flags/waffle-feature-flags.service';
import * as _ from 'lodash-es';

declare let Bokeh_v0123: any;

const CURVE_METRICS_ORDER = ['min_lactate_eq', 'lt_1', 'anaerobic_threshold', 'mmss', 'vo2_max_power', 'max_aerobic'];

const Defaults = {
  BorderColor: '#BBBBBB',
  Chart: {
    AxisLabelTextColor: '#444444',
    BackgroundFillColor: '#FFF',
    BorderFillColor: '#FFF',
    FullWidth: 1160,
    HalfWidth: 580,
    Height: 380,
    TitleColor: '#222222',
  },
  CrosshairLineColor: '#888888',
  GridLineColor: '#BBBBBB',
  LabelTextColor: '#444444',
  Legend: {
    BackgroundFillAlpha: 0.75,
    BorderWidth: 0,
  },
  TestDataChartHeight: 500,
};

@Injectable({
  providedIn: 'root',
})
export class LactateTestDataGraphsService {
  private readonly customRendererService = inject(CustomRendererService);
  private featureFlagsService: WaffleFeatureFlagsService = inject(WaffleFeatureFlagsService);
  private readonly plottingAPI = Bokeh_v0123.Plotting;
  private isPFMCLactate: FeatureFlagValue;

  constructor() {
    this.isPFMCLactate = this.featureFlagsService.getValue(FeatureFlagEnum.PFMC_LACTATE);
  }

  destroyChart(elementId: string): void {
    this.customRendererService.emptyAndCleanupBokeh(elementId);
  }

  renderMetabolicDemand(
    metabolicPlot6: Record<string, any>,
    test: TestDto,
    overrideElementId?: string,
    isPdfRender = false,
    scaleText = 1
  ): void {
    const elementId = overrideElementId || ChartConstants.METABOLIC_DEMAND.ELEMENT_ID;
    this.customRendererService.emptyAndCleanupBokeh(elementId);
    this.generateMetabolicDemand(metabolicPlot6, elementId, test.id, test, isPdfRender, scaleText);
  }

  renderLactateAccumulation(
    metabolicPlot6: Record<string, any>,
    test: TestDto,
    overrideElementId?: string,
    isPdfRender = false,
    scaleText = 1
  ): void {
    const elementId = overrideElementId || ChartConstants.LACTATE_ACCUMULATION.ELEMENT_ID;
    this.customRendererService.emptyAndCleanupBokeh(elementId);
    this.generateLactateAccumulation(metabolicPlot6, elementId, test.id, test, isPdfRender, scaleText);
  }

  private generateMetabolicDemand(
    athleteData: any,
    elementId: any,
    testId: any,
    test: any,
    isPdfRender = false,
    scaleText = 1
  ): void {
    let data = athleteData;
    let listPlot: any[] = [];
    const fontSizeValue = 9;

    const metabolidDemandPlotData = data.plots.find(
      (p: any) => (p.title_text ?? '') === 'Determination of metabolic demand'
    );
    if (!metabolidDemandPlotData) {
      console.warn('Cannot find metabolic demand data');
      return;
    }

    this.generateMetabolicDemandPlot(metabolidDemandPlotData, testId, listPlot, test, isPdfRender, scaleText);
    this.plottingAPI.show(listPlot[0], elementId);

    setTimeout((): void => {
      const targetElement: Element | null = this.customRendererService.select(elementId);
      const childNodes: NodeListOf<Element> | undefined = this.customRendererService.getChildNodes(
        targetElement,
        "div[class*='bk-root']"
      );
      const metabolicDemandTitleText = this.customRendererService.getChildNodes(
        targetElement,
        'div#metabolic-demand-title-text'
      );

      if (childNodes?.length && !metabolicDemandTitleText?.length) {
        const containerEle = childNodes[childNodes.length - 1];
        let htmlText: string = `<div id="metabolic-demand-title-text" class="chart-text-header" style="font-size: ${fontSizeValue + 3}px; line-height:1.5em">`;
        htmlText += '<b>Determination of metabolic demand</b>';
        htmlText +=
          `<div style="font-size: ${fontSizeValue + 2}px; line-height:1.5em">${metabolidDemandPlotData.header_text}</div>` +
          '</div>';
        this.customRendererService.insertBefore(targetElement, htmlText, containerEle);
      }
    });
  }

  private generateLactateAccumulation(
    athleteData: any,
    elementId: any,
    testId: any,
    test: any,
    isPdfRender = false,
    scaleText = 1
  ): void {
    let data = athleteData;
    let listPlot: any[] = [];
    const fontSizeValue = 9;

    const lactateAccumulationPlotData = data.plots.find((p: any) =>
      (p.title_text ?? '').startsWith('Lactate curves using optimized')
    );
    if (!lactateAccumulationPlotData) {
      console.warn('Cannot find lactate accumulation data');
      return;
    }

    this.generateLactateAccumulationPlot(lactateAccumulationPlotData, testId, listPlot, test, isPdfRender, scaleText);
    this.plottingAPI.show(listPlot[0], elementId);

    setTimeout((): void => {
      const targetElement: Element | null = this.customRendererService.select(elementId);
      console.log(targetElement);
      const childNodes: NodeListOf<Element> | undefined = this.customRendererService.getChildNodes(
        targetElement,
        "div[class*='bk-root']"
      );
      const lactateAccumulationTitleText = this.customRendererService.getChildNodes(
        targetElement,
        'div#lactate-accumulation-title-text'
      );

      if (childNodes?.length && !lactateAccumulationTitleText?.length) {
        const containerEle = childNodes[childNodes.length - 1];
        let htmlText: string = `<div id="lactate-accumulation-title-text" class="chart-text-header" style="font-size: ${fontSizeValue + 3}px; line-height:1.5em">`;
        htmlText += '<b>Determination of lactate accumulation</b>';
        htmlText +=
          `<div style="font-size: ${fontSizeValue + 2}px; line-height:1.5em">${lactateAccumulationPlotData.header_text}</div>` +
          '</div>';
        this.customRendererService.insertBefore(targetElement, htmlText, containerEle);
      }
    });
  }

  private generateMetabolicDemandPlot(
    rowData: any,
    testId: any,
    listPlot: any,
    test: any,
    isPdfRender = false,
    scaleText = 1
  ): void {
    let TOOLS: string = 'pan,box_zoom,reset';
    const idx = 0;
    const fontSizeValue = 9;

    let p: any,
      x_range: boolean = false,
      y_range;
    const xKeyStorage: string = 'TDXsettings';
    const yKeyStorage: string = 'TDYsettings';
    let extra_x_ranges = undefined;

    if (!rowData.isReset) {
      let testDataXVal: any = localStorage.getItem(`${xKeyStorage}${idx}_${testId}`);
      if (testDataXVal) {
        testDataXVal = JSON.parse(testDataXVal);
        if (testDataXVal) {
          x_range = new Bokeh_v0123.Range1d({
            start: testDataXVal.plot_range[0],
            end: testDataXVal.plot_range[1],
          });
        }
      }

      let testDataYVal: any = localStorage.getItem(`${yKeyStorage}${idx}_${testId}`);
      if (testDataYVal) {
        testDataYVal = JSON.parse(testDataYVal);
        if (testDataYVal) {
          y_range = new Bokeh_v0123.Range1d({
            start: testDataYVal.plot_range[0],
            end: testDataYVal.plot_range[1],
          });
        }
      }
    }

    const plotData: any = {
      plot_width: Defaults.Chart.FullWidth,
      plot_height: Defaults.TestDataChartHeight,
      tools: TOOLS,
      background_fill_color: Defaults.Chart.BackgroundFillColor,
      border_fill_color: Defaults.Chart.BorderFillColor,
      outline_line_color: Defaults.BorderColor,
      sizing_mode: 'scale_width',
    };
    if (isPdfRender) {
      plotData.tools = [];
    }

    if (rowData.y_range) {
      y_range = new Bokeh_v0123.Range1d({
        start: rowData.y_range.plot_range[0],
        end: rowData.y_range.plot_range[1],
      });
    }
    if (x_range) {
      plotData.x_range = x_range;
    }

    if (y_range) {
      plotData.y_range = y_range;
    }

    // IAT-588 Lactate Accumulation - Test Curves > apply min/max of sport settings to x-axis
    if (test.is_show_test_data && !plotData.x_range && rowData.x_range) {
      if (rowData.circles && rowData.circles.length > 0) {
        _.each(rowData.circles, function (circle): void {
          if (rowData.x_range.plot_range[0] > circle.x[0]) {
            rowData.x_range.plot_range[0] = Math.floor(circle.x[0]);
          }
          if (rowData.x_range.plot_range[1] < circle.x[0]) {
            rowData.x_range.plot_range[1] = Math.ceil(circle.x[0]);
          }
        });
      }
      plotData.x_range = this.createRange(rowData.x_range);
    }

    this.mappingDataDF(test, rowData);
    this.initExtraXRanges(extra_x_ranges, plotData, test, false);

    p = this.plottingAPI.figure(plotData);

    this.addSecondaryXAxis(test, p, rowData.data_df, false, scaleText);
    this.handleRowDataLine(rowData, p);
    this.handleRowDatacircles(rowData, p);

    const testDataCallbackCode = 'saveSettingsForTD(cb_obj,' + idx + ');';
    p.x_range.callback = new Bokeh_v0123.CustomJS({
      code: testDataCallbackCode,
    });

    const testDataYCallbackCode = 'saveYSettingsForTD(cb_obj,' + idx + ');';
    p.y_range.callback = new Bokeh_v0123.CustomJS({
      code: testDataYCallbackCode,
    });

    p.yaxis.axis_label = rowData['y_axis_label'];
    p.xaxis.axis_label = rowData['x_axis_label'] || 'Seconds';
    p.title.text_color = Defaults.Chart.TitleColor;
    p._legend.location = rowData['legend_location'] || 'top_left';
    p._legend.background_fill_color = Defaults.Chart.BackgroundFillColor;
    p._legend.background_fill_alpha = Defaults.Legend.BackgroundFillAlpha;
    p._legend.label_text_color = Defaults.LabelTextColor;
    p._legend.label_text_font_size = `${fontSizeValue}pt`;
    p.toolbar_location = 'right';

    //override chart axis attributes
    p.yaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
    p.yaxis.major_label_text_font_size = `${fontSizeValue}pt`;
    p.yaxis.axis_label_text_font_style = 'normal';
    p.yaxis.axis_label_text_color = Defaults.LabelTextColor;
    p.yaxis.major_label_text_color = Defaults.LabelTextColor;

    p.xaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
    p.xaxis.major_label_text_font_size = `${fontSizeValue}pt`;
    p.xaxis.axis_label_text_font_style = 'normal';
    p.xaxis.axis_label_text_color = Defaults.LabelTextColor;
    p.xaxis.major_label_text_color = Defaults.LabelTextColor;

    if (test) {
      // Hide original X axis
      p.xaxis.visible = false;
    }

    p.xgrid.grid_line_color = Defaults.GridLineColor;
    p.ygrid.grid_line_color = Defaults.GridLineColor;

    if (p.right) {
      _.each(p.right, (right): void => {
        right.attributes.axis_label_text_font_style = 'normal';
        right.attributes.major_label_text_font_size = 'normal';
        right.attributes.axis_label_text_font_size = `${fontSizeValue}pt`;
        right.attributes.axis_label_text_color = Defaults.LabelTextColor;
        right.attributes.major_label_text_color = Defaults.LabelTextColor;
      });
    }

    if (p.left) {
      _.each(p.left, (left): void => {
        left.attributes.axis_label_text_font_size = `${fontSizeValue}pt`;
        left.attributes.major_label_text_font_size = `${fontSizeValue}pt`;
        left.attributes.axis_label_text_font_style = 'normal';
        left.attributes.axis_label_text_color = Defaults.LabelTextColor;
        left.attributes.major_label_text_color = Defaults.LabelTextColor;
      });
    }

    listPlot.push(p);
  }

  private generateLactateAccumulationPlot(
    rowData: any,
    testId: any,
    listPlot: any,
    test: any,
    isPdfRender = false,
    scaleText = 1
  ): void {
    let TOOLS: string = 'pan,box_zoom,reset';
    const idx = 0;
    const fontSizeValue = 9;

    let p: any,
      x_range: boolean = false,
      y_range;
    const xKeyStorage: string = 'TDXsettings';
    const yKeyStorage: string = 'TDYsettings';
    let extra_x_ranges = undefined;

    if (!rowData.isReset) {
      let testDataXVal: any = localStorage.getItem(`${xKeyStorage}${idx}_${testId}`);
      if (testDataXVal) {
        testDataXVal = JSON.parse(testDataXVal);
        if (testDataXVal) {
          x_range = new Bokeh_v0123.Range1d({
            start: testDataXVal.plot_range[0],
            end: testDataXVal.plot_range[1],
          });
        }
      }

      let testDataYVal: any = localStorage.getItem(`${yKeyStorage}${idx}_${testId}`);
      if (testDataYVal) {
        testDataYVal = JSON.parse(testDataYVal);
        if (testDataYVal) {
          y_range = new Bokeh_v0123.Range1d({
            start: testDataYVal.plot_range[0],
            end: testDataYVal.plot_range[1],
          });
        }
      }
    }

    const plotData: any = {
      plot_width: Defaults.Chart.FullWidth,
      plot_height: Defaults.TestDataChartHeight,
      tools: TOOLS,
      background_fill_color: Defaults.Chart.BackgroundFillColor,
      border_fill_color: Defaults.Chart.BorderFillColor,
      outline_line_color: Defaults.BorderColor,
      sizing_mode: 'scale_width',
    };
    if (isPdfRender) {
      plotData.tools = [];
    }

    if (rowData.y_range) {
      y_range = new Bokeh_v0123.Range1d({
        start: rowData.y_range.plot_range[0],
        end: rowData.y_range.plot_range[1],
      });
    }
    if (x_range) {
      plotData.x_range = x_range;
    }

    if (y_range) {
      plotData.y_range = y_range;
    }

    // IAT-588 Lactate Accumulation - Test Curves > apply min/max of sport settings to x-axis
    if (test.is_show_test_data && !plotData.x_range && rowData.x_range) {
      if (rowData.circles && rowData.circles.length > 0) {
        _.each(rowData.circles, function (circle): void {
          if (rowData.x_range.plot_range[0] > circle.x[0]) {
            rowData.x_range.plot_range[0] = Math.floor(circle.x[0]);
          }
          if (rowData.x_range.plot_range[1] < circle.x[0]) {
            rowData.x_range.plot_range[1] = Math.ceil(circle.x[0]);
          }
        });
      }
      plotData.x_range = this.createRange(rowData.x_range);
    }

    this.mappingDataDF(test, rowData);
    this.initExtraXRanges(extra_x_ranges, plotData, test, false);

    p = this.plottingAPI.figure(plotData);

    this.addSecondaryXAxis(test, p, rowData.data_df, false, scaleText);
    this.handleRowDataLine(rowData, p);
    this.handleRowDatacircles(rowData, p);
    this.addCurveMetrics(p, rowData, scaleText);

    const testDataCallbackCode = 'saveSettingsForTD(cb_obj,' + idx + ');';
    p.x_range.callback = new Bokeh_v0123.CustomJS({
      code: testDataCallbackCode,
    });

    const testDataYCallbackCode = 'saveYSettingsForTD(cb_obj,' + idx + ');';
    p.y_range.callback = new Bokeh_v0123.CustomJS({
      code: testDataYCallbackCode,
    });

    p.yaxis.axis_label = rowData['y_axis_label'];
    p.xaxis.axis_label = rowData['x_axis_label'] || 'Seconds';
    p.title.text_color = Defaults.Chart.TitleColor;
    p._legend.location = rowData['legend_location'] || 'top_left';
    p._legend.background_fill_color = Defaults.Chart.BackgroundFillColor;
    p._legend.background_fill_alpha = Defaults.Legend.BackgroundFillAlpha;
    p._legend.label_text_color = Defaults.LabelTextColor;
    p._legend.label_text_font_size = `${fontSizeValue}pt`;
    p.toolbar_location = 'right';

    //override chart axis attributes
    p.yaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
    p.yaxis.major_label_text_font_size = `${fontSizeValue}pt`;
    p.yaxis.axis_label_text_font_style = 'normal';
    p.yaxis.axis_label_text_color = Defaults.LabelTextColor;
    p.yaxis.major_label_text_color = Defaults.LabelTextColor;

    p.xaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
    p.xaxis.major_label_text_font_size = `${fontSizeValue}pt`;
    p.xaxis.axis_label_text_font_style = 'normal';
    p.xaxis.axis_label_text_color = Defaults.LabelTextColor;
    p.xaxis.major_label_text_color = Defaults.LabelTextColor;

    if (test) {
      // Hide original X axis
      p.xaxis.visible = false;
    }

    p.xgrid.grid_line_color = Defaults.GridLineColor;
    p.ygrid.grid_line_color = Defaults.GridLineColor;

    if (p.right) {
      _.each(p.right, (right): void => {
        right.attributes.axis_label_text_font_style = 'normal';
        right.attributes.major_label_text_font_size = 'normal';
        right.attributes.axis_label_text_font_size = `${fontSizeValue}pt`;
        right.attributes.axis_label_text_color = Defaults.LabelTextColor;
        right.attributes.major_label_text_color = Defaults.LabelTextColor;
      });
    }

    if (p.left) {
      _.each(p.left, (left): void => {
        left.attributes.axis_label_text_font_size = `${fontSizeValue}pt`;
        left.attributes.major_label_text_font_size = `${fontSizeValue}pt`;
        left.attributes.axis_label_text_font_style = 'normal';
        left.attributes.axis_label_text_color = Defaults.LabelTextColor;
        left.attributes.major_label_text_color = Defaults.LabelTextColor;
      });
    }

    listPlot.push(p);
  }

  private createRange(range: { plot_range: any[]; bounds: any }) {
    return new Bokeh_v0123.Range1d({
      start: range.plot_range[0],
      end: range.plot_range[1],
      bounds: range.bounds,
    });
  }

  private mappingDataDF(test: any, data: any): void {
    if (!test) {
      return;
    }

    if (!data.data_df) {
      data.data_df = {};
    }

    if (test.data_df_power) {
      data.data_df['power'] = test.data_df_power;
    }

    if (test.data_df_vms) {
      data.data_df['vms'] = test.data_df_vms;
    }

    if (!data.data_df['x']) {
      if (test.sport.simulation_type == AppConstants.SIMULATE_TYPE.POWER) {
        data.data_df['x'] = test.data_df_power;
      } else if (test.sport.simulation_type == AppConstants.SIMULATE_TYPE.SPEED) {
        data.data_df['x'] = test.data_df_vms;
      }
    }
  }

  private initExtraXRanges(extra_x_ranges: any, chart: any, test: any, has_secondary_x: boolean = true): void {
    if (extra_x_ranges) {
      chart.extra_x_ranges = extra_x_ranges;
    } else {
      chart.extra_x_ranges = {
        primary: new Bokeh_v0123.Range1d({
          start: chart.x_range.min,
          end: chart.x_range.max,
        }),
      };

      if (test?.sport?.secondary_type && has_secondary_x) {
        chart.extra_x_ranges['secondary'] = new Bokeh_v0123.Range1d({
          start: chart.x_range.min,
          end: chart.x_range.max,
        });
      }
    }
  }

  private addSecondaryXAxis(test: any, chart: any, data: any, has_secondary_x: boolean = true, scaleText = 1): void {
    const fontSizeValue = 9;
    if (test) {
      const primary_label: string = this.findXAxisLabel(test.sport.primary_type);
      const secondary_label: string = this.findXAxisLabel(test.sport.secondary_type);
      const common_layout = {
        axis_label_text_font_size: `${fontSizeValue}pt`,
        major_label_text_font_size: `${fontSizeValue}pt`,
        axis_label_text_font_style: 'normal',
        axis_label_text_color: Defaults.LabelTextColor,
        major_label_text_color: Defaults.LabelTextColor,
      };
      let primary_layout: any = {
        x_range_name: 'primary',
        axis_label: primary_label,
        ...common_layout,
        formatter: new Bokeh_v0123.FuncTickFormatter({
          code: `
          let listDataX = ${JSON.stringify(data)};
          return calculateTicker(tick, ${test.sport.primary_type}, ${
            test.sport.simulation_type
          }, ${test.mass}, listDataX);`,
        }),
      };

      chart.add_layout(new Bokeh_v0123.LinearAxis(primary_layout), 'below');

      if (test?.sport?.secondary_type && has_secondary_x) {
        let secondary_layout: any = {
          x_range_name: 'secondary',
          axis_label: secondary_label,
          ...common_layout,
          formatter: new Bokeh_v0123.FuncTickFormatter({
            code: `
            let listDataX = ${JSON.stringify(data)};
            return calculateTicker(tick, ${test.sport.secondary_type}, ${
              test.sport.simulation_type
            }, ${test.mass}, listDataX);`,
          }),
        };

        chart.add_layout(new Bokeh_v0123.LinearAxis(secondary_layout), 'below');
      }
    }
  }

  private handleRowDataLine(rowData: any, p: any): void {
    if (!rowData?.lines?.length) {
      return;
    }
    _.each(rowData.lines, (line): void => {
      let ds = { x: line.x, y: line.y };

      let source = new Bokeh_v0123.ColumnDataSource({ data: ds });

      switch (line.legend) {
        case 'Messwerte':
          line.legend = 'Measured values';
          break;
        case 'Berechnet':
          line.legend = 'Calculated';
          break;
      }

      let glyph = p.line({
        x: { field: 'x' },
        y: { field: 'y' },
        source: source,
        line_color: line.line_color,
        line_width: 2,
        legend: line.legend,
      });
      if (line.line_dash == 'dashed') {
        glyph.attributes.glyph.line_dash = [5, 6];
      }
      if (line.line_dash == 'dotted') {
        glyph.attributes.glyph.line_dash = [2, 4];
      }
    });
  }

  private handleRowDatacircles(rowData: any, p: any): void {
    if (rowData.circles && rowData.lines.length > 0) {
      _.each(rowData.circles, (circle) => {
        let ds = {
          x: circle.x,
          y: circle.y,
        };

        let source = new Bokeh_v0123.ColumnDataSource({ data: ds });

        p.circle({
          x: { field: 'x' },
          y: { field: 'y' },
          fill_color: circle.fill_color,
          line_color: circle.line_color,
          size: circle.size,
          source: source,
          legend: circle.legend,
        });
      });
    }
  }

  private addCurveMetrics(plot: any, data: any, scaleText = 1): void {
    const fontSizeValue = 9;
    const data_metrics: Array<CurveMetric> = data.curve_metrics;
    if (data_metrics == null || !Array.isArray(data_metrics) || data_metrics.length === 0) {
      return;
    }

    const metricsMap = new Map(data_metrics.map((m) => [m.slug, m]));
    const curve_metrics: Array<CurveMetric> = CURVE_METRICS_ORDER.map((slug) => metricsMap.get(slug)).filter(
      Boolean
    ) as Array<CurveMetric>;

    const maxCurveMetrics = curve_metrics.length > 6 ? 6 : curve_metrics.length;
    const yMax = 4 + 0.3 * (maxCurveMetrics - 1);

    curve_metrics.forEach((curve_metric, index) => {
      const verticalLine = new Bokeh_v0123.Span({
        location: curve_metric.value,
        dimension: 'height',
        line_color: curve_metric.color ?? 'black',
        line_width: 1.5,
        line_dash: [],
      });
      plot.add_layout(verticalLine);

      const yOffset = yMax + index * 0.6;
      const lineLabel = new Bokeh_v0123.Label({
        x: curve_metric.value,
        y: yOffset,
        text: `${curve_metric.name} `,
        text_color: curve_metric.color ?? 'black',
        text_font_size: `${fontSizeValue}pt`,
        text_align: 'right',
        text_baseline: 'bottom',
      });
      plot.add_layout(lineLabel);
    });
  }

  private findXAxisLabel(id: number): string {
    const s_power = _.find(AppConstants.REFERENCE_SYSTEMS_POWER, { id: id });
    const s_speed = _.find(AppConstants.REFERENCE_SYSTEMS_SPEED, { id: id });
    let label: string = '';
    if (s_power) {
      label = s_power.unit;
    } else if (s_speed) {
      label = s_speed.unit;
    }
    return label;
  }
}
